/**
 * Blog seed posts — see docs/BLOG_PAGE.md
 * Keep this shape stable if migrating to Contentful / Neon later.
 */

export const BLOG_CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'product', label: 'Product' },
  { id: 'field', label: 'Field' },
  { id: 'market', label: 'Market' },
];

const ALL_BLOG_POSTS = [
  {
    slug: 'utah-sb284-detached-adu-housing-supply',
    hidden: true,
    title: 'Utah’s backyard ADU mandate: what SB 284 actually changes',
    lede:
      'An update on Utah housing-supply legislation after the 2026 session — detached ADU status, the October 2026 city deadline, and how this sits beside the AEI reform scorecard.',
    seoTitle: 'Utah SB 284 Detached ADU Law — Housing Supply Update | Construx',
    seoDescription:
      'Construx market note on Utah SB 284: detached ADUs on lots ~11,000 sq ft and up, city code updates by October 1, 2026, local standards that remain, and HB 492 infrastructure funding. Analysis, not legal advice.',
    keywords:
      'Utah SB 284, detached ADU, accessory dwelling unit Utah, HB 492, housing supply, October 2026 ADU ordinance, Wasatch Front ADU, Construx',
    category: 'market',
    date: '2026-07-17',
    dateModified: '2026-07-17',
    cover: '/blog/reform-wave/cover.svg',
    coverAlt: 'Utah housing supply legislation update — Construx field notes',
    about: [
      { '@type': 'Thing', name: 'Accessory dwelling units' },
      { '@type': 'Thing', name: 'Utah housing legislation' },
      { '@type': 'Thing', name: 'Housing supply' },
    ],
    geoSummary: [
      'Utah SB 284 (2026) requires qualifying municipalities to permit detached accessory dwelling units on lots or parcels of about 11,000 square feet or larger that already contain a single-family dwelling where that dwelling is a permitted use.',
      'Cities covered by the mandate must have conforming land-use regulations in place by approximately October 1, 2026; many Utah County cities were amending codes mid-2026.',
      'Local governments still set setbacks, height, design, and limited parking or owner-occupancy rules within state bounds — they cannot simply ban detached ADUs on qualifying lots.',
      'HB 492 creates a State Housing Infrastructure Partnership Fund (on the order of $100 million) aimed at water, sewer, and road infrastructure that unlocks stalled housing supply.',
      'Construx’s interactive AEI reform-wave post (June 2, 2026 source data) scored Utah HB 184 (starter-home zoning exception) as failed; SB 284 is the separate, enacted detached-ADU pathway. This note is analysis, not legal advice.',
    ],
    body: [
      {
        type: 'p',
        text: 'Utah’s 2026 legislative session moved housing supply on two tracks that matter for backyard units and regional capacity. This note summarizes status as of mid-July 2026. It is industry analysis for homeowners and builders — not legal advice. Always confirm the ordinance for your city and lot.',
      },
      {
        type: 'h2',
        text: 'Housing supply context',
      },
      {
        type: 'p',
        text: 'Utah has carried a multi-year housing shortage measured in tens of thousands of units relative to demand (commonly cited around ~40,000 in market commentary). Statewide zoning and infrastructure bills will not close that gap overnight. Detached ADUs matter because they add dwellings on land that already has streets and utilities — one lot at a time — while infrastructure funds try to unlock larger subdivisions that are approved but stuck.',
      },
      {
        type: 'h2',
        text: 'SB 284 — detached ADUs (status: enacted / local compliance underway)',
      },
      {
        type: 'p',
        text: 'Senate Bill 284 is the session’s clearest backyard-housing change. Qualifying municipalities must adopt land-use rules that permit a detached accessory dwelling unit on any lot or parcel that is about 11,000 square feet or larger and already contains a single-family dwelling, when that single-family use is permitted on the parcel.',
      },
      {
        type: 'ul',
        items: [
          'Legislation / topic: SB 284 — detached ADUs (often abbreviated DADUs)',
          'Status: Enacted in the 2026 session; cities amending ordinances through mid-2026',
          'Compliance target: Local regulations generally due by October 1, 2026',
          'Lot floor in the mandate: ~11,000 sq ft (cities may allow smaller lots; they cannot keep a higher floor that blocks the state requirement)',
          'Still local: setbacks, height, massing, design standards, and limited parking / owner-occupancy rules within state bounds',
          'Not a free-for-all: building codes, utilities, and site constraints still apply',
        ],
      },
      {
        type: 'p',
        text: 'Coverage is for municipalities that fall under Utah’s moderate-income housing / population-tier rules — not every tiny town. Secondary summaries sometimes say “cities over 5,000”; treat that as a shorthand and verify whether your city is in scope.',
      },
      {
        type: 'p',
        text: 'Reporting from Utah County shows cities such as Lehi updating codes (for example, separation from the primary dwelling and owner-occupancy of one of the units), while others were still drafting. Eligibility maps and lot-coverage rules will vary city by city even after the state floor is met.',
      },
      {
        type: 'h2',
        text: 'HB 492 — infrastructure for new supply (status: enacted funding vehicle)',
      },
      {
        type: 'p',
        text: 'House Bill 492 creates a State Housing Infrastructure Partnership Fund — on the order of $100 million — to help cities finance regional infrastructure (water, sewer, roads) that new housing needs. That is not an ADU bill. It is a supply bill aimed at projects that already have land entitlement but lack pipes and asphalt.',
      },
      {
        type: 'ul',
        items: [
          'Legislation / topic: HB 492 — State Housing Infrastructure Partnership Fund',
          'Status: Enacted funding framework from the 2026 session',
          'Mechanism: State partnership capital for city infrastructure that unlocks housing capacity',
          'Relevance to ADUs: Indirect — more regional supply over years; backyard ADUs remain the on-lot path',
        ],
      },
      {
        type: 'h2',
        text: 'How this relates to our AEI reform-wave post',
      },
      {
        type: 'p',
        text: 'Our interactive field note, “The 2026 housing reform wave,” walks the AEI Housing Center’s June 2, 2026 State Housing Supply Legislation Update. In that dataset, Utah appears mainly through HB 184 — a starter-home / smaller-lot zoning exception that failed. AEI’s Utah card therefore reads as a story about local discretion and a failed bill.',
      },
      {
        type: 'p',
        text: 'SB 284 is a different, enacted pathway: a detached-ADU mandate with a dated city-compliance deadline. The reform-wave interactive now shows it as legal capacity (unmodeled) with a Construx provisional homes/yr contribution to the combined path — separate from AEI’s published 279,820. Use this update for SB 284 / HB 492 status, and the reform-wave piece for the multi-state scorecard.',
      },
      {
        type: 'h2',
        text: 'What this means for Construx readers',
      },
      {
        type: 'p',
        text: 'For Utah homeowners on qualifying lots in covered cities, the legal floor for a detached backyard unit is rising — but the practical path still runs through local setbacks, parking, utilities, and permitting. For builders and partners, expect a wave of ordinance updates into fall 2026, then a longer ramp of applications once rules are published.',
      },
      {
        type: 'p',
        text: 'Construx’s interest is the same as elsewhere: when policy opens backyard capacity, a digitally designed, kit-of-parts envelope can help local GCs deliver predictable shells. Product and delivery still have to clear city rules — legislation only opens the gate.',
      },
      {
        type: 'h2',
        text: 'Sources',
      },
      {
        type: 'ul',
        items: [
          'Utah League of Cities and Towns — 2026 Land Use Recap (SB 284 detached ADU notes; Oct 1, 2026)',
          'Utah County coverage of city ordinance updates under S.B. 284 (Daily Herald, July 2026)',
          'AEI Housing Center — 2026 State Housing Supply Legislation Update (June 2, 2026) for the multi-state scorecard / HB 184',
          'Construx interactive: /blog/the-2026-housing-reform-wave',
        ],
      },
    ],
  },
  {
    slug: 'the-2026-housing-reform-wave',
    title: 'The 2026 housing reform wave',
    lede:
      'Thirty-one state legislatures moved on housing supply this year. A guided read of what actually passed, what stalled, what died — and how many homes the AEI Housing Center estimates the wave could unlock.',
    seoTitle: 'The 2026 Housing Reform Wave — AEI Scorecard Explained | Construx',
    seoDescription:
      'Construx field notes on the AEI 2026 housing supply scorecard (279,820 modeled homes/yr), refreshed July 17, 2026 to add Utah SB 284 as enacted legal capacity with a Construx provisional 2,800 homes/yr contribution (combined path 282,620) — kept separate from AEI’s published total.',
    keywords:
      '2026 housing reform, AEI Housing Center, state housing supply legislation, ADU legalization, zoning reform, lot size reform, housing shortage, Construx analysis',
    category: 'market',
    date: '2026-07-16',
    dateModified: '2026-07-17',
    cover: '/blog/reform-wave/cover.svg',
    coverAlt: 'The 2026 Reform Wave — Construx industry analysis',
    interactive: true,
    interactiveSrc: '/blog/reform-wave/index.html',
    citation: {
      '@type': 'CreativeWork',
      name: '2026 State Housing Supply Legislation Update',
      author: 'Ed Pinto, AEI Housing Center',
      datePublished: '2026-06-02',
      url: 'https://www.aei.org/centers/housing-center/',
    },
    about: [
      { '@type': 'Thing', name: 'Housing supply legislation' },
      { '@type': 'Thing', name: 'Accessory dwelling units' },
      { '@type': 'Thing', name: 'Zoning reform' },
    ],
    geoSummary: [
      'AEI Housing Center tracked 31 states and 160 bills (191 including twin House/Senate versions) in the 2026 state housing supply legislation update (Ed Pinto, June 2, 2026).',
      'Playbook-aligned active, passed, or enacted reforms model to an estimated 279,820 homes per year — a conditional estimate, not a construction forecast.',
      'Construx July 17, 2026 refresh: six states with enacted aligned reform (ID, WA, KS, VA, ME, UT). Utah SB 284 is enacted detached-ADU legal capacity; AEI left it unquantified, so Construx attributes a provisional 2,800 homes/yr as a third supply form. AEI published active estimate stays 279,820; combined path is 282,620.',
      'Nine hold pending bills modeling to 215,940 homes/yr; failed bills carried 219,955 homes/yr of potential.',
      'ADU-only legislation accounts for about 48% of the AEI active estimate (135,160 homes/yr); with Utah provisional, ADU-linked supply is about 137,960 homes/yr.',
      'Construx publishes this as analysis of the public AEI report plus a documented post-AEI Utah update — not advocacy scoring of the politics.',
    ],
    body: [],
  },
  {
    slug: 'precision-envelope-central-texas',
    hidden: true,
    title: 'Precision envelopes for Central Texas backyards',
    lede:
      'How Construx turns a digital model into a weather-tight frame — square, flush, and ready for finish-out in weeks, not seasons.',
    category: 'product',
    date: '2026-06-18',
    cover: '/why/system/hero-envelope.jpg',
    coverAlt: 'Interior view of a Construx precision envelope frame',
    body: [
      {
        type: 'p',
        text: 'Most backyard builds still start with stick framing and hope. Construx starts with a digital model: every stud, opening, and connection engineered before a saw touches lumber on site.',
      },
      {
        type: 'h2',
        text: 'Designed in software, framed as a system',
      },
      {
        type: 'p',
        text: 'The kit-of-parts envelope arrives as a coordinated set of panels and members. Field crews assemble to the build guide — not invent structure under a Texas sky. That is how we cut schedule risk and keep the shell square.',
      },
      {
        type: 'p',
        text: 'For homeowners, that means a backyard office, guest suite, or flex space that feels intentional from day one. For builders, it means predictable scope and fewer callbacks on the shell.',
      },
      {
        type: 'h2',
        text: 'Built with a local partner',
      },
      {
        type: 'p',
        text: 'Construx designs the system. A licensed build partner in your market handles permitting, assembly, and finish. You get factory-level precision with a contractor who already knows Central Texas codes and yards.',
      },
    ],
  },
  {
    slug: 'adu-timeline-what-to-expect',
    hidden: true,
    title: 'What a Construx ADU timeline actually looks like',
    lede:
      'From first call to framed shell: a clear path for homeowners who want a backyard unit without a year of construction fog.',
    category: 'field',
    date: '2026-05-28',
    cover: '/backyard/frame-room.jpg',
    coverAlt: 'Framed Construx backyard room during assembly',
    body: [
      {
        type: 'p',
        text: 'Homeowners usually ask two things: how long, and what happens when. Here is the field-facing answer for a typical Construx backyard project in Central Texas.',
      },
      {
        type: 'h2',
        text: 'Design lock and documentation',
      },
      {
        type: 'p',
        text: 'Once the program is set — office, guest suite, or flex — we lock the digital model and produce the documentation your builder needs for permitting and assembly. Changes after lock are possible, but they cost schedule. We say that up front.',
      },
      {
        type: 'h2',
        text: 'Kit delivery and shell assembly',
      },
      {
        type: 'p',
        text: 'Precision framing ships to site and goes up fast relative to stick-built ADUs. The goal is a weather-tight envelope so finish trades can work cleanly. Your local GC owns the site day-to-day.',
      },
      {
        type: 'p',
        text: 'Exact weeks vary by permit office, site conditions, and finish scope. What does not vary is the order of operations: model → kit → shell → finish.',
      },
    ],
  },
  {
    slug: 'why-local-builders-matter',
    hidden: true,
    title: 'Why local builders still matter in a digital build',
    lede:
      'Software and kits do not replace the GC — they give your builder a sharper tool. Here is how the partnership works.',
    category: 'market',
    date: '2026-05-10',
    cover: '/why/system/finished-to-spec.jpg',
    coverAlt: 'Finished Construx build ready for occupancy',
    body: [
      {
        type: 'p',
        text: 'Prefabrication myths often skip the hard part: permits, neighbors, soil, utilities, and finish taste. That is still local work. Construx partners with licensed general contractors so the system lands in real yards.',
      },
      {
        type: 'h2',
        text: 'Construx designs. Partners deliver.',
      },
      {
        type: 'p',
        text: 'We own the digital design and the precision envelope. The partner owns the relationship on the ground — inspections, subcontractors, and the walkthrough that makes a homeowner comfortable.',
      },
      {
        type: 'p',
        text: 'If you are a homeowner, start with our Partners directory. If you are a builder interested in the network, reach out through Contact — we are growing across Central Texas.',
      },
    ],
  },
  {
    slug: 'guest-suite-vs-office',
    hidden: true,
    title: 'Guest suite or backyard office — choosing the right model',
    lede:
      'Same Construx envelope system, different programs. A short guide to picking the starting point that fits how you live.',
    category: 'product',
    date: '2026-04-22',
    cover: '/why/system/guest-suite.png',
    coverAlt: 'Construx guest suite exterior at dusk',
    body: [
      {
        type: 'p',
        text: 'Every Construx model is engineered as a precision system. The decision is less about “can we build it” and more about “what will you use it for every week.”',
      },
      {
        type: 'h2',
        text: 'Backyard office',
      },
      {
        type: 'p',
        text: 'Quiet work, clients, or creative space — usually simpler wet areas and a layout optimized for focus. Think daylight, power, and a door that closes on the main house.',
      },
      {
        type: 'h2',
        text: 'Guest suite',
      },
      {
        type: 'p',
        text: 'Sleeping, bathing, and a bit of living — more plumbing and finish, more like a small apartment. Ideal for visiting family or longer stays without giving up the primary home.',
      },
      {
        type: 'p',
        text: 'Browse the Models catalog for starting points, then talk with a build partner about site setbacks, utilities, and finish level.',
      },
    ],
  },
  {
    slug: 'kit-of-parts-on-site',
    hidden: true,
    title: 'What “kit of parts” looks like on site',
    lede:
      'Panels, members, and a build guide — not a shipping container. A field note on how Construx assemblies actually go up.',
    category: 'field',
    date: '2026-03-30',
    cover: '/why/system/cnc-kit.jpg',
    coverAlt: 'Precision-cut Construx kit components',
    body: [
      {
        type: 'p',
        text: '“Kit of parts” means the structure is nested, labeled, and sequenced. Crews follow a construction guide instead of field-cutting a one-off frame from scratch.',
      },
      {
        type: 'h2',
        text: 'Fewer surprises in the shell',
      },
      {
        type: 'p',
        text: 'Openings land where the model says they land. Connections are designed, not improvised. That is the difference between a backyard project that drifts and one that stays on the critical path.',
      },
      {
        type: 'p',
        text: 'Finish still takes craft — paint, tile, fixtures. Construx does not claim to erase that. We claim a cleaner, faster path to a weather-tight envelope so finish can shine.',
      },
    ],
  },
  {
    slug: 'central-texas-housing-pressure',
    hidden: true,
    title: 'Backyard units and Central Texas housing pressure',
    lede:
      'ADUs will not solve every housing problem — but they are one of the few tools that add space without waiting on greenfield lots.',
    category: 'market',
    date: '2026-03-12',
    cover: '/backyard/hero-office.jpg',
    coverAlt: 'Construx backyard office exterior',
    body: [
      {
        type: 'p',
        text: 'Central Texas keeps adding households faster than it adds gentle-density housing. Backyard units give homeowners a way to host family, reclaim work space, or create rental potential where zoning allows.',
      },
      {
        type: 'h2',
        text: 'Speed and certainty matter',
      },
      {
        type: 'p',
        text: 'When labor is scarce and schedules slip, a digitally designed envelope helps builders protect margin and homeowners protect sanity. That is the market case for Construx — not novelty for its own sake.',
      },
      {
        type: 'p',
        text: 'Curious whether your lot works? Start with Contact, or find a Partners builder already operating in your market.',
      },
    ],
  },
];

// Mocked seed posts stay in the file but are excluded everywhere (index, direct
// URLs, related posts, SEO) until real content replaces them.
// See docs/BLOG_PAGE.md — "Hidden posts".
export const BLOG_POSTS = ALL_BLOG_POSTS.filter((post) => !post.hidden);

export function getPostBySlug(slug) {
  return BLOG_POSTS.find((post) => post.slug === slug) || null;
}

export function getRelatedPosts(slug, limit = 2) {
  const current = getPostBySlug(slug);
  if (!current) return BLOG_POSTS.slice(0, limit);
  const sameCategory = BLOG_POSTS.filter(
    (post) => post.slug !== slug && post.category === current.category,
  );
  const pool = sameCategory.length
    ? sameCategory
    : BLOG_POSTS.filter((post) => post.slug !== slug);
  return pool.slice(0, limit);
}

export function formatPostDate(iso) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(`${iso}T12:00:00`));
  } catch {
    return iso;
  }
}
